'use client'

import { useEffect, useRef, useState } from 'react'
import { useOrganization } from '@clerk/nextjs'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { AlertCircle, Globe2, Plus } from 'lucide-react'
import type {
    ICompanyDomain,
    ICompanyDomainRefreshResult,
} from '@/api/organization/company-domain/get-company-domain-query'
import { useRetryCompanyDomainMutation } from '@/api/organization/company-domain/retry-company-domain-mutation'
import { Button } from '@/components/ui/button'
import { SettingsSection } from '@/features/settings-modal/settings-section'
import { CompanyDomainAddModal } from '@/features/settings-modal/tabs/organization/sections/company-domain-add-modal'
import { CompanyDomainManagementConfirmationModal } from '@/features/settings-modal/tabs/organization/sections/company-domain-management-confirmation-modal'
import { CompanyDomainRow } from '@/features/settings-modal/tabs/organization/sections/company-domain-row'
import { toast } from '@/features/toast/toast'
import {
    applyConfirmedCompanyDomainClaim,
    getBackendConfirmedCompanyDomainName,
    getSuccessfulCompanyDomainRefreshData,
    hasAuthoritativeCompanyDomainClaim,
    type IConfirmedCompanyDomainClaim,
} from '@/features/settings-modal/tabs/organization/utils/claim-company-domain'
import {
    getCompanyDomainFailureMessage,
    isPreparedCompanyDomainOrganizationActive,
} from '@/features/settings-modal/tabs/organization/utils/company-domain-claim'
import { useCompanyDomainManagement } from '@/features/settings-modal/tabs/organization/utils/use-company-domain-management'

interface ICompanyDomainSectionProps {
    companyDomain: ICompanyDomain
    refreshCompanyDomain: () => Promise<ICompanyDomainRefreshResult>
    canManage: boolean
}

type ICompanyDomainClaimIntent = 'add' | 'turn_on_suggestions' | 'retry_setup'

function normalizeDomainName(domainName: string) {
    return domainName.trim().toLowerCase()
}

/** Renders one authoritative list for adding and managing multiple Clerk domains. */
export function CompanyDomainSection({ companyDomain, refreshCompanyDomain, canManage }: ICompanyDomainSectionProps) {
    const { organization } = useOrganization()
    const prefersReducedMotion = useReducedMotion()
    const claimMutation = useRetryCompanyDomainMutation()
    const activeOrganizationIdRef = useRef(organization?.id)
    activeOrganizationIdRef.current = organization?.id
    const claimOperationVersionRef = useRef(0)
    const [isAddModalOpen, setIsAddModalOpen] = useState(false)
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null)
    const [submittingDomain, setSubmittingDomain] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [confirmedClaim, setConfirmedClaim] = useState<IConfirmedCompanyDomainClaim | null>(null)
    const {
        displayDomains: managedDomains,
        preparedAction: preparedManagementAction,
        errorMessage: managementErrorMessage,
        isMutationPending: isManagementMutationPending,
        isBusy: isManagementBusy,
        prepareAction: prepareManagementAction,
        confirmAction: handleManagementConfirm,
        closeConfirmation: closeManagementConfirmation,
        resetManagementState,
    } = useCompanyDomainManagement({
        activeOrganizationId: organization?.id,
        companyDomain,
        refreshCompanyDomain,
        canManage,
        isClaimSubmitting: submittingDomain !== null,
        onPreparationStarted: () => setErrorMessage(null),
        onPreparationFailed: setErrorMessage,
        onConfirmed: () => setErrorMessage(null),
    })
    const displayDomains = applyConfirmedCompanyDomainClaim(managedDomains, organization?.id, confirmedClaim)
    const displayedDomainNames = new Set(displayDomains.map((domain) => normalizeDomainName(domain.name)))
    const displayedCandidates = companyDomain.add.candidates.filter(
        (candidate) => !displayedDomainNames.has(normalizeDomainName(candidate.name))
    )
    const displayedCompanyDomain: ICompanyDomain = {
        ...companyDomain,
        domains: displayDomains,
        add: {
            ...companyDomain.add,
            candidates: displayedCandidates,
            canAttempt: companyDomain.add.canAttempt && displayedCandidates.length > 0,
        },
    }
    const hasUnresolvedConfirmation = confirmedClaim !== null || preparedManagementAction !== null
    const isBusy = submittingDomain !== null || claimMutation.isPending || isManagementBusy || hasUnresolvedConfirmation

    useEffect(() => {
        claimOperationVersionRef.current += 1
        setIsAddModalOpen(false)
        setSelectedDomain(null)
        setSubmittingDomain(null)
        setErrorMessage(null)
        setConfirmedClaim(null)
    }, [organization?.id])

    useEffect(() => {
        if (
            confirmedClaim !== null &&
            confirmedClaim.organizationId === organization?.id &&
            hasAuthoritativeCompanyDomainClaim(companyDomain.domains, confirmedClaim.domainName)
        ) {
            setConfirmedClaim(null)
        }
    }, [companyDomain.domains, confirmedClaim, organization?.id])

    useEffect(() => {
        if (!isAddModalOpen) return
        const selectedStillAvailable = displayedCandidates.some((candidate) => candidate.name === selectedDomain)
        if (!selectedStillAvailable) {
            setSelectedDomain(displayedCandidates[0]?.name ?? null)
        }
    }, [displayedCandidates, isAddModalOpen, selectedDomain])

    const handleAddModalOpenChange = (open: boolean) => {
        if (isBusy) return
        setErrorMessage(null)
        setIsAddModalOpen(open)
        if (open) setSelectedDomain(displayedCandidates[0]?.name ?? null)
    }

    const markClaimConfirmed = (organizationId: string, domainName: string, intent: ICompanyDomainClaimIntent) => {
        setErrorMessage(null)
        resetManagementState()
        setConfirmedClaim({ organizationId, domainName })
        if (intent === 'add') setIsAddModalOpen(false)
        toast.success(
            intent === 'add'
                ? `${domainName} added to Claimed domains.`
                : intent === 'retry_setup'
                  ? `Domain setup confirmed for ${domainName}.`
                  : `Suggestions turned on for ${domainName}.`
        )
    }

    const handleClaimDomain = async (domainName: string, intent: ICompanyDomainClaimIntent) => {
        const preparedOrganizationId = organization?.id
        const normalizedDomain = normalizeDomainName(domainName)
        const attachedDomain = displayDomains.find((domain) => normalizeDomainName(domain.name) === normalizedDomain)
        const isEligibleAddCandidate = displayedCandidates.some(
            (candidate) => normalizeDomainName(candidate.name) === normalizedDomain
        )
        const canSubmit =
            intent === 'add'
                ? displayedCompanyDomain.add.canAttempt && isEligibleAddCandidate && !attachedDomain
                : attachedDomain?.actions.canTurnOnSuggestions === true

        if (!canManage || !canSubmit || !preparedOrganizationId || isBusy) return

        const claimOperationVersion = ++claimOperationVersionRef.current
        setSubmittingDomain(normalizedDomain)
        setErrorMessage(null)
        resetManagementState()
        try {
            const result = await claimMutation.mutateAsync({
                organizationId: preparedOrganizationId,
                domain: normalizedDomain,
            })
            if (!isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, preparedOrganizationId)) {
                return
            }

            const confirmedDomain = getBackendConfirmedCompanyDomainName(result)
            if (confirmedDomain === normalizedDomain) {
                markClaimConfirmed(preparedOrganizationId, normalizedDomain, intent)
                await refreshCompanyDomain().catch(() => null)
                return
            }

            await refreshCompanyDomain().catch(() => null)
            if (!isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, preparedOrganizationId)) {
                return
            }
            // A received backend failure remains a failure even when the domain exists: a
            // different postcondition, such as suppression cleanup, may still need retry.
            setErrorMessage(getCompanyDomainFailureMessage(result))
        } catch {
            const refreshed = await refreshCompanyDomain().catch(() => null)
            if (!isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, preparedOrganizationId)) {
                return
            }
            const refreshedData = refreshed ? getSuccessfulCompanyDomainRefreshData(refreshed) : undefined
            if (refreshedData && hasAuthoritativeCompanyDomainClaim(refreshedData.domains, normalizedDomain)) {
                markClaimConfirmed(preparedOrganizationId, normalizedDomain, intent)
                return
            }
            setErrorMessage("We couldn't confirm the company domain. Refresh the status and try again.")
        } finally {
            if (
                claimOperationVersionRef.current === claimOperationVersion &&
                isPreparedCompanyDomainOrganizationActive(activeOrganizationIdRef.current, preparedOrganizationId)
            ) {
                setSubmittingDomain(null)
            }
        }
    }

    return (
        <>
            <SettingsSection
                title="Claimed domains"
                description="Manage which verified company email domains can discover this workspace."
                cta={
                    <Button
                        variant="ds2-secondary"
                        size="ds2"
                        disabled={!canManage || isBusy}
                        onClick={() => handleAddModalOpenChange(true)}
                    >
                        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                        Add domain
                    </Button>
                }
            >
                <div className="overflow-hidden rounded-xl border border-border bg-background">
                    <AnimatePresence initial={false} mode="popLayout">
                        {displayDomains.length === 0 ? (
                            <motion.div
                                key="empty-domains"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.2 }}
                                className="overflow-hidden"
                            >
                                <div className="flex items-start gap-3 px-4 py-5">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                                        <Globe2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-foreground">No domains claimed</p>
                                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                            Add an eligible administrator domain so coworkers can discover this
                                            workspace.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            displayDomains.map((domain, index) => (
                                <motion.div
                                    key={normalizeDomainName(domain.name)}
                                    layout="position"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={
                                        prefersReducedMotion
                                            ? { duration: 0 }
                                            : {
                                                  opacity: { duration: 0.3 },
                                                  height: { type: 'spring', mass: 0.1 },
                                              }
                                    }
                                    className="overflow-hidden"
                                >
                                    <div className={index > 0 ? 'border-t border-border' : undefined}>
                                        <CompanyDomainRow
                                            domain={domain}
                                            canManage={canManage}
                                            isBusy={isBusy}
                                            onTurnOnSuggestions={(selected) =>
                                                void handleClaimDomain(
                                                    selected.name,
                                                    selected.enrollmentMode !== 'manual_invitation' &&
                                                        selected.automaticClaimStatus !== 'enabled'
                                                        ? 'retry_setup'
                                                        : 'turn_on_suggestions'
                                                )
                                            }
                                            onTurnOffSuggestions={(selected) =>
                                                void prepareManagementAction('turn_off_suggestions', selected)
                                            }
                                            onRemove={(selected) =>
                                                void prepareManagementAction('remove_domain', selected)
                                            }
                                        />
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </AnimatePresence>
                </div>

                {errorMessage && !isAddModalOpen && (
                    <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        <p className="text-sm text-destructive" role="alert">
                            {errorMessage}
                        </p>
                    </div>
                )}
            </SettingsSection>

            <CompanyDomainAddModal
                open={isAddModalOpen}
                companyDomain={displayedCompanyDomain}
                selectedDomain={selectedDomain}
                isSubmitting={submittingDomain !== null}
                errorMessage={errorMessage}
                onOpenChange={handleAddModalOpenChange}
                onSelectedDomainChange={(domain) => {
                    setSelectedDomain(domain)
                    setErrorMessage(null)
                }}
                onConfirm={async () => {
                    if (!selectedDomain) return
                    await handleClaimDomain(selectedDomain, 'add')
                }}
            />

            <CompanyDomainManagementConfirmationModal
                preparedAction={preparedManagementAction}
                errorMessage={managementErrorMessage}
                confirmDisabled={
                    !canManage ||
                    isManagementMutationPending ||
                    !preparedManagementAction ||
                    preparedManagementAction.organizationId !== organization?.id
                }
                onOpenChange={(open) => {
                    if (open) return
                    closeManagementConfirmation()
                }}
                onConfirm={handleManagementConfirm}
            />
        </>
    )
}
